import {
    Component,
    ElementRef,
    HostBinding,
    Inject,
    OnInit,
    OnDestroy,
    ViewChild,
    AfterViewInit,
    AfterViewChecked,
    NgZone,
} from '@angular/core';
import {MatIconButton} from '@angular/material/button';
import {BackendService} from '@global/services/backend.service';
import {StorageService} from '@global/services/storage.service';
import {Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {BreakpointObserver} from '@angular/cdk/layout';
import {CdkDrag} from '@angular/cdk/drag-drop';

import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UserPermissionsService} from '@global/services/user-permissions.service';
import {Listentry} from '@shared/listentry';

type ChatMessage = {
    text: string;
    isUser: boolean;
    icon?: string;
    ts: number;
};

type ChatEntityType = 'institution' | 'person';

type ChatContextData = {
    entityId: number;
    entityType: ChatEntityType;
};

type SuggestionItem = {label: string; prompt: string};

type ResizeHandle = 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface DialogBounds {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface ResizeSession {
    handle: ResizeHandle;
    startX: number;
    startY: number;
    rectLeft: number;
    rectTop: number;
    rectRight: number;
    rectBottom: number;
    dragStartX: number;
    dragStartY: number;
}

type StreamingChannel = 'anonymized' | 'restored';

interface StreamingChannelState {
    message: ChatMessage;
    buffer: string;
    interval: number | null;
    done: boolean;
    prefix: string;
    visible: boolean;
}

// Narrow type helper to access private _elementRef in a type-safe manner without 'any'
interface HasPrivateElementRef {
    _elementRef?: {nativeElement?: {disabled?: boolean}};
}

@Component({
    selector: 'phscw-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss'],
    standalone: false,
    preserveWhitespaces: true,
})

export class ChatComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {

    @HostBinding('class.large-screen') isLargeScreen = false;

    /** Chat input + send button refs */
    @ViewChild('input') input!: ElementRef<HTMLTextAreaElement>;
    @ViewChild('button') button!: MatIconButton;
    @ViewChild(CdkDrag) dragRefDirective!: CdkDrag<HTMLDivElement>;

    /** Entity context (controls what backend data is loaded) */
    readonly entityId: number;
    readonly entityType: ChatEntityType;

    /** Chat state */
    chatStarted = false;
    chatFinished = false;
    responseInProgress = false;
    componentReady = false;
    private userScrolled = false;
    messages: ChatMessage[] = [];
    inputFocused = false;
    copiedMessageIndex: number | null = null;
    ttsLoadingMessageIndex: number | null = null;
    speakingMessageIndex: number | null = null;
    audioPlaying = false;
    messageActionsEnabled = true;
    private audioElement: HTMLAudioElement | null = null;
    private audioObjectUrl: string | null = null;
    private copyResetTimer: number | null = null;
    private ttsRequestSub: Subscription | null = null;
    private ttsStreamCancel: (() => void) | null = null;
    private readonly ttsMaxChars = 4096;

    // Cancellation state for ongoing answer
    private typingInterval: number | null = null;
    private currentResponseSub: Subscription | null = null;
    private currentResponseMsg: ChatMessage | null = null;
    private streamingStates = new Map<StreamingChannel, StreamingChannelState>();
    private streamingActive = false;

    private readonly minDialogWidth = 320;
    private readonly minDialogHeight = 320;
    private readonly edgeSize = 8;
    private overlayPane: HTMLElement | null = null;
    private resizeSession: ResizeSession | null = null;
    private savedCursor: string | null = null;
    private savedUserSelect: string | null = null;
    private readonly resizeMouseMove = (event: MouseEvent) => this.onResizeMouseMove(event);
    private readonly resizeMouseUp = (event: MouseEvent) => this.stopResize(event);
    private resizeTeardownFns: Array<() => void> = [];

    suggestions: SuggestionItem[] = [];
    /** Model / system selection */
    selectedModel: string = 'gpt-5-mini';
    selectedSystem: 'chatgpt5' | 'chatgpt' | 'local' | '' = 'chatgpt5';
    proAnalyzeEnabled = false;

    /** Life-cycle teardown */
    private destroyed$ = new Subject<void>();

    /** Browser speech API reference */
    // Settings flags (legacy names kept for compatibility)
    debugMode = false;
    ovewriteMode = false;
    anonymizeMode = true;

    /** Backing state for older multi-select approach (kept for compatibility if needed) */
    activeSettings: string[] = [];
    private previousResponseId: string | null = null;

    constructor(
        private readonly backendService: BackendService,
        private readonly storageService: StorageService,
        private readonly observer: BreakpointObserver,
        private readonly ngZone: NgZone,
        private readonly host: ElementRef<HTMLElement>,

        private readonly dialogRef: MatDialogRef<ChatComponent>,
        private readonly userPermissions: UserPermissionsService,
        @Inject(MAT_DIALOG_DATA) private readonly chatContext: ChatContextData,
    ) {
        this.entityId = Number(this.chatContext?.entityId) || 0;
        this.entityType = this.chatContext?.entityType ?? 'institution';
    }

    //* Lifecycle
    ngOnInit(): void {
        this.initializeEventSubscriptions();

        // Seed legacy array so any older UI bound to it stays in sync
        this.activeSettings = [
            ...(this.debugMode ? ['debug'] : []),
            ...(this.ovewriteMode ? ['overwrite'] : []),
            ...(this.anonymizeMode ? ['anon'] : []),
        ];
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.componentReady = true;
            this.input?.nativeElement?.focus();
            this.overlayPane = this.resolveOverlayPane();
            this.initializeResizeListeners();
        }, 50);
    }

    ngAfterViewChecked(): void {
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.stopResize();
        if (this.resizeTeardownFns.length) {
            for (const teardown of this.resizeTeardownFns.splice(0)) {
                try {
                    teardown();
                } catch {
                    /* noop */
                }
            }
        }
        this.cancelTts();
        if (this.copyResetTimer) {
            window.clearTimeout(this.copyResetTimer);
            this.copyResetTimer = null;
        }
        this.stopAudio();
    }

    private initializeEventSubscriptions(): void {
        this.observer
            .observe(['(min-width: 960px)'])
            .pipe(takeUntil(this.destroyed$))
            .subscribe((state) => (this.isLargeScreen = state.matches));

        // Suggestions depend on entity type (not route)
        const listName: 'institutionsPrompt' | 'peoplePrompt' =
            this.entityType === 'person' ? 'peoplePrompt' : 'institutionsPrompt';
        this.loadSuggestions(listName);
    }

    private loadSuggestions(promptType: 'institutionsPrompt' | 'peoplePrompt'): void {
        const promptStorageKey = `listentries|${promptType}`;
        this.storageService
            .getItem<Listentry[]>(promptStorageKey)
            .then((rawPromptEntries) => {
                if (!rawPromptEntries || !rawPromptEntries.length) {
                    this.suggestions = [];
                    return;
                }

                // Sort suggestions by their list_sort value, falling back to original sequence when no valid sort order is defined.
                const processedSuggestions = rawPromptEntries
                    .map((entry, index) => ({entry, index}))
                    .filter(({entry}) => String(entry.deleted ?? 'N') === 'N')
                    .sort((first, second) => {
                        const firstSortOrder = this.normalizeSortOrder(first.entry.list_sort);
                        const secondSortOrder = this.normalizeSortOrder(second.entry.list_sort);
                        const firstHasSort = firstSortOrder !== null;
                        const secondHasSort = secondSortOrder !== null;

                        if (firstHasSort && secondHasSort && firstSortOrder !== secondSortOrder) {
                            return firstSortOrder - secondSortOrder;
                        }
                        if (firstHasSort && !secondHasSort) return -1;
                        if (!firstHasSort && secondHasSort) return 1;

                        return first.index - second.index;
                    })
                    .map(({entry}) => {
                        const suggestionLabel = this.normalizeListEntryLabel(entry);
                        let rawPromptData: unknown = entry.list_data;

                        if (rawPromptData && typeof rawPromptData !== 'string') {
                            try {
                                rawPromptData = JSON.stringify(rawPromptData);
                            } catch {
                                rawPromptData = String(rawPromptData);
                            }
                        }

                        const formattedPromptText = String(rawPromptData ?? suggestionLabel).trim();
                        return {
                            label: suggestionLabel || formattedPromptText,
                            prompt: formattedPromptText,
                        } as SuggestionItem;
                    })
                    .filter((suggestion) => suggestion.label && suggestion.prompt);

                this.suggestions = processedSuggestions;
            })
            .catch((error) => {
                console.error(`Failed to load ${promptType} suggestions:`, error);
                this.suggestions = [];
            });
    }

    /** @returns Normalized label string from list_value or list_key (trimmed). */
    private normalizeListEntryLabel(entry: Listentry): string {
        return String(entry.list_value ?? entry.list_key ?? '').trim();
    }

    // Interpret list_sort values as finite numbers; return null when unset or invalid.
    private normalizeSortOrder(sortValue: unknown): number | null {
        if (sortValue === undefined || sortValue === null || sortValue === '') return null;
        const numericValue = Number(sortValue);
        return Number.isFinite(numericValue) ? numericValue : null;
    }

    trackSuggestion(index: number, item: SuggestionItem): string {
        return item.prompt ?? `${index}`;
    }


    /*
     * ────────────────────────────────────────────────────────────────────────────
     * Engine + model selection (menu-based; no evt guards)
     * ────────────────────────────────────────────────────────────────────────────
     */

    selectSystem(system: 'chatgpt5' | 'chatgpt' | 'local'): void {
        this.selectedSystem = system;
        this.selectedModel =
            system === 'chatgpt5' ? 'gpt-5' :
                system === 'chatgpt' ? 'gpt-4o' :
                    'tinyllama:latest';
        this.proAnalyzeEnabled = this.selectedModel === 'gpt-5';
    }

    selectModel(model: string): void {
        this.selectedModel = model;
        this.proAnalyzeEnabled = this.selectedModel === 'gpt-5';
    }

    toggleProAnalyze(): void {
        this.proAnalyzeEnabled = !this.proAnalyzeEnabled;
        this.selectedSystem = 'chatgpt5';
        this.selectedModel = this.proAnalyzeEnabled ? 'gpt-5' : 'gpt-5-mini';
    }

    onCopyResult(success: boolean, messageIndex: number): void {
        if (!success) return;
        this.copiedMessageIndex = messageIndex;
        if (this.copyResetTimer) {
            window.clearTimeout(this.copyResetTimer);
        }
        this.copyResetTimer = window.setTimeout(() => {
            this.copiedMessageIndex = null;
        }, 2000);
    }

    onSpeakMessage(message: ChatMessage, messageIndex: number): void {
        if (this.ttsLoadingMessageIndex === messageIndex) {
            this.cancelTts();
            return;
        }
        if (this.speakingMessageIndex === messageIndex && this.audioPlaying) {
            this.stopAudio();
            return;
        }

        const rawText = this.getMessagePlainText(message);
        if (!rawText) return;

        const safeText = Array.from(rawText).slice(0, this.ttsMaxChars).join('');
        if (!safeText.trim()) return;

        this.stopAudio();

        if (this.canStreamTts()) {
            this.playAudioStream(safeText, messageIndex);
            return;
        }

        this.ttsLoadingMessageIndex = messageIndex;
        this.ttsRequestSub = this.backendService
            .getFilePost('ArtificialIntelligence/tts', {text: safeText})
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (audioBlob) => {
                    this.ttsRequestSub = null;
                    this.ttsLoadingMessageIndex = null;
                    this.playAudio(audioBlob, messageIndex);
                },
                error: (err) => {
                    this.ttsRequestSub = null;
                    this.ttsLoadingMessageIndex = null;
                    console.error('AI TTS failed', err);
                },
            });
    }

    private cancelTts(): void {
        if (this.ttsRequestSub) {
            this.ttsRequestSub.unsubscribe();
            this.ttsRequestSub = null;
        }
        if (this.ttsStreamCancel) {
            this.ttsStreamCancel();
            this.ttsStreamCancel = null;
        }
        this.ttsLoadingMessageIndex = null;
    }

    resolveSpeakIcon(messageIndex: number): string {
        if (this.ttsLoadingMessageIndex === messageIndex) {
            return 'spinner';
        }
        if (this.speakingMessageIndex === messageIndex && this.audioPlaying) {
            return 'stop';
        }
        return 'volume-up';
    }

    /*
     * ────────────────────────────────────────────────────────────────────────────
     * Input & sending
     * ────────────────────────────────────────────────────────────────────────────
     */

    onEnter(event: KeyboardEvent, _inputEl: HTMLTextAreaElement): void {
        if (event.shiftKey) {
            return;
        }
        event.preventDefault();
        this.onSendButtonClick();
    }

    // Settings menu row click handlers to toggle checkboxes even when clicking empty space
    onToggleDebug(event: MouseEvent): void {
        event?.stopPropagation?.();
        this.debugMode = !this.debugMode;
    }

    onToggleOverwrite(event: MouseEvent): void {
        event?.stopPropagation?.();
        this.ovewriteMode = !this.ovewriteMode;
    }

    onToggleAnonymize(event: MouseEvent): void {
        event?.stopPropagation?.();
        this.anonymizeMode = !this.anonymizeMode;
    }

    onSendButtonClick(): void {
        const value = this.input?.nativeElement?.value ?? '';
        if (this.responseInProgress && !value.trim()) {
            this.cancelOngoingResponse();
            this.input?.nativeElement?.focus();
            return;
        }

        this.send(value);
    }

    send(input: string, displayText?: string): void {
        if (!input.trim()) return;

        const debugAtSend = this.debugMode;

        // If AI is currently answering, capture the partial answer text
        const partialPrev = this.getPartialAIAnswer();

        // If there is an ongoing answer being typed or requested, cancel it
        this.cancelOngoingResponse();

        const wasFirstMessage = !this.chatStarted;
        this.chatStarted = true;
        this.userScrolled = false;
        this.chatFinished = false;
        this.responseInProgress = true;

        // Show displayText in chat if provided, otherwise show input
        const userDisplayText = this.normalizePlain(displayText || input);
        this.messages.push({text: userDisplayText, isUser: true, ts: Date.now()});
        this.messages.push({text: '...', isUser: false, icon: 'spinner', ts: Date.now()});

        this.resetInput();
        this.input?.nativeElement?.focus();

        // Always send input to AI, regardless of what's displayed in chat
        const effectiveInput = partialPrev ? `${input}\n\n--- Bisherige unvollständige KI-Antwort ---\n${partialPrev}\n--- Ende ---` : input;

        this.getResponse(effectiveInput, debugAtSend);

        if (wasFirstMessage) {
            setTimeout(() => {
                this.setupScrollListener();
                this.scrollToMessage();
            }, 100);
        } else {
            setTimeout(() => this.scrollToMessage(), 100);
        }
    }

    private resetInput(): void {
        const el = this.input?.nativeElement;
        if (!el) return;
        el.value = '';
        el.style.height = 'auto';
        el.rows = 1;
    }

    @ViewChild('scrollContainer') private scrollContainer!: ElementRef<HTMLDivElement>;

    private scrollToMessage(): void {
        if (this.userScrolled) {
            return;
        }

        const el = this.scrollContainer?.nativeElement;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }

    private setupScrollListener(): void {
        const scrollEl = this.scrollContainer?.nativeElement;
        if (!scrollEl) return;

        const handler = () => {
            const isAtBottom = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 5;
            this.userScrolled = !isAtBottom;
        };

        // Initial evaluation and listener
        handler();
        scrollEl.addEventListener('scroll', handler);
    }

    /** Permission: show settings (gear) menu in chat */
    get allowAIChatSettings(): boolean {

        try {
            const alt = this.userPermissions.getPermissionValue('enableAiChatSettings');
            if (typeof alt === 'boolean') return alt;
            if (alt !== null && alt !== undefined) return !!alt;
        } catch { /* noop */ }

        return false;
    }

    /*
     * ────────────────────────────────────────────────────────────────────────────
     * Backend + rendering
     * ────────────────────────────────────────────────────────────────────────────
     */

    private getResponse(input: string, debugAtSend: boolean = this.debugMode): void {
        const streamingEnabled = !debugAtSend && this.selectedSystem !== 'local';
        const payload = {
            prompt: input,
            context: {entityId: this.entityId, entityType: this.entityType},
            debug: debugAtSend,
            overwrite: this.ovewriteMode,
            gptmodel: this.selectedModel,
            system: this.selectedSystem,
            anonymize: this.anonymizeMode,
            previous_response_id: this.previousResponseId || null,
            stream: streamingEnabled,
        };

        if (streamingEnabled) {
            const observable = this.backendService
                .postRequestStream('ArtificialIntelligence/read', payload)
                .pipe(takeUntil(this.destroyed$));

            this.currentResponseSub = observable.subscribe({
                next: (event) => this.handleStreamEvent(event),
                error: (err) => this.handleStreamFailure(err, debugAtSend),
                complete: () => this.handleStreamCompleteSignal(),
            });
            return;
        }

        const observable = this.backendService
            .postRequestAsync('ArtificialIntelligence/read', payload)
            .pipe(takeUntil(this.destroyed$));

        this.currentResponseSub = observable.subscribe({
            next: (response) => {
                const raw = response?.data ?? '';
                const msg = this.mdWithBr(raw);
                this.messages = this.messages.filter((m) => m.text !== '...');
                this.displayTypewriterEffect(msg, debugAtSend);

                const respId = this.getResponseId(response);
                if (respId) {
                    this.previousResponseId = respId;
                }
            },
            error: (err) => this.handleStreamFailure(err, debugAtSend),
        });
    }

    private displayTypewriterEffect(message: string, debugAtSend: boolean = this.debugMode): void {
        // Ensure old typer is stopped before starting a new one
        if (this.typingInterval) {
            clearInterval(this.typingInterval);
            this.typingInterval = null;
        }

        const response: ChatMessage = {text: '', isUser: false, ts: Date.now()};
        this.messages.push(response);
        this.currentResponseMsg = response;

        if (debugAtSend) {
            response.text = this.normalizeMd(message);
            this.chatFinished = true;
            this.responseInProgress = false;
            this.currentResponseMsg = null;
            setTimeout(() => this.scrollToMessage(), 100);
            return;
        }

        let index = 0;
        this.typingInterval = window.setInterval(() => {
            if (index < message.length) {
                const chunkSize = Math.min(2 + Math.floor(Math.random() * 3), message.length - index);
                response.text += message.substring(index, index + chunkSize);
                index += chunkSize;

                if (index % 30 === 0) setTimeout(() => this.scrollToMessage(), 5);
            } else {
                if (this.typingInterval) {
                    clearInterval(this.typingInterval);
                    this.typingInterval = null;
                }
                response.text = this.normalizeMd(response.text);
                this.chatFinished = true;
                this.responseInProgress = false;
                this.currentResponseMsg = null;
                setTimeout(() => this.scrollToMessage(), 100);
            }
        }, 15);
    }

    private handleStreamEvent(event: any): void {
        if (!event || typeof event !== 'object') return;
        const type = String(event.type ?? '').toLowerCase();
        const channel = this.resolveStreamChannel(event.channel);
        const isReplace = event?.replace === true ||
            (typeof event.mode === 'string' && event.mode.toLowerCase() === 'replace');

        switch (type) {
            case 'chunk':
                if (isReplace || channel !== 'anonymized') {
                    this.applyStreamingReplace(channel, event);
                } else {
                    this.onStreamAppendChunk(channel, event);
                }
                break;
            case 'complete':
                if (isReplace || channel !== 'anonymized') {
                    this.applyStreamingReplace(channel, {...event, type: 'complete'});
                } else {
                    this.onStreamComplete(channel, event);
                }
                break;
            case 'error':
                this.onStreamErrorEvent(event);
                break;
            default:
                break;
        }
    }

    private resolveStreamChannel(raw: unknown): StreamingChannel {
        const value = typeof raw === 'string' ? raw.toLowerCase() : '';
        if (value === 'anonymized' || value === 'sanitized') {
            return 'anonymized';
        }
        return 'restored';
    }

    private onStreamAppendChunk(channel: StreamingChannel, event: any): void {
        const delta = typeof event.delta === 'string' ? event.delta : '';
        if (!delta) return;

        this.streamingActive = true;
        const normalized = this.mdWithBr(delta);
        this.appendStreamingDelta(channel, normalized);

        const respId = this.getResponseId(event);
        if (respId) {
            this.previousResponseId = respId;
        }
    }

    private onStreamComplete(channel: StreamingChannel, event: any): void {
        const respId = this.getResponseId(event);
        if (respId) {
            this.previousResponseId = respId;
        }
        const full = typeof event.full === 'string' ? event.full : '';
        this.markStreamingComplete(channel, full);
    }

    private onStreamErrorEvent(event: any): void {
        console.error('AI streaming error event', event);
        const message = typeof event.message === 'string' && event.message.trim() ?
            event.message :
            'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
        this.handleStreamFailure(message, false, message);
    }

    private ensureStreamingState(channel: StreamingChannel): StreamingChannelState {
        let state = this.streamingStates.get(channel);
        if (state) {
            if (!state.interval) {
                state.interval = window.setInterval(() => this.drainStreamingBuffer(channel), 18);
            }
            return state;
        }

        const visible = channel === 'restored';
        if (visible) {
            this.removeSpinnerPlaceholder();
        }

        const prefix = '';
        const message: ChatMessage = {text: prefix, isUser: false, ts: Date.now()};
        if (visible) {
            this.messages.push(message);
        }

        state = {
            message,
            buffer: '',
            interval: window.setInterval(() => this.drainStreamingBuffer(channel), 18),
            done: false,
            prefix,
            visible,
        };
        this.streamingStates.set(channel, state);
        this.streamingActive = true;
        return state;
    }

    private appendStreamingDelta(channel: StreamingChannel, text: string): void {
        if (!text) return;
        const state = this.ensureStreamingState(channel);
        state.buffer += text;
        this.drainStreamingBuffer(channel);
    }

    private applyStreamingReplace(channel: StreamingChannel, event: any): void {
        const fullText = typeof event.full === 'string' ? this.mdWithBr(event.full) : '';
        const state = this.ensureStreamingState(channel);
        const normalized = this.normalizeMd(fullText);
        const current = this.getChannelContent(state);

        if (!normalized.length) {
            state.message.text = state.prefix;
            state.buffer = '';
        } else {
            const keepLength = this.longestCommonPrefixLength(current, normalized);
            const keepSegment = normalized.substring(0, keepLength);
            const remainder = normalized.substring(keepLength);
            state.message.text = state.prefix + keepSegment;
            state.buffer = remainder;
        }

        if (!state.interval) {
            state.interval = window.setInterval(() => this.drainStreamingBuffer(channel), 18);
        }

        const isComplete = event?.type === 'complete' || event?.done === true;
        state.done = isComplete;
        this.streamingStates.set(channel, state);

        if (state.buffer.length === 0) {
            if (state.done) {
                this.finalizeStreamingChannel(channel, state);
            }
        } else {
            this.drainStreamingBuffer(channel);
        }
    }

    private markStreamingComplete(channel: StreamingChannel, fullText?: string): void {
        if (typeof fullText === 'string' && fullText) {
            this.applyStreamingReplace(channel, {full: fullText, type: 'complete', replace: true, done: true});
            return;
        }

        const state = this.ensureStreamingState(channel);
        state.done = true;
        this.drainStreamingBuffer(channel, true);
    }

    private drainStreamingBuffer(channel: StreamingChannel, force: boolean = false): void {
        const state = this.streamingStates.get(channel);
        if (!state) return;

        if (state.buffer.length === 0) {
            if (state.done) {
                this.finalizeStreamingChannel(channel, state);
            }
            return;
        }

        const iterations = force ? 512 : 1;
        for (let i = 0; i < iterations && state.buffer.length > 0; i += 1) {
            let available = state.buffer.length;

            if (!force && channel === 'restored') {
                const sanitizedState = this.streamingStates.get('anonymized');
                if (sanitizedState) {
                    const sanitizedVisible = this.getChannelContent(sanitizedState).length;
                    const restoredVisible = this.getChannelContent(state).length;
                    const allowedRaw = sanitizedVisible - restoredVisible;
                    const allowed = Math.max(0, allowedRaw) + 1;
                    if (allowed <= 0) {
                        return;
                    }
                    available = Math.min(available, allowed);
                }
            }

            if (available <= 0) {
                return;
            }

            const chunkSize = force ?
                available :
                Math.min(2 + Math.floor(Math.random() * 3), available);
            const piece = state.buffer.substring(0, chunkSize);
            state.buffer = state.buffer.substring(chunkSize);
            state.message.text += piece;

            if (!force) {
                break;
            }
        }

        if ((state.message.text.length % 30) === 0) {
            setTimeout(() => this.scrollToMessage(), 5);
        }

        if (force && state.buffer.length === 0 && state.done) {
            this.finalizeStreamingChannel(channel, state);
        }
    }

    private finalizeStreamingChannel(channel: StreamingChannel, state?: StreamingChannelState): void {
        const currentState = state ?? this.streamingStates.get(channel);
        if (!currentState) return;
        if (currentState.interval) {
            clearInterval(currentState.interval);
            currentState.interval = null;
        }
        currentState.message.text = this.normalizeMd(currentState.message.text);
        currentState.done = true;

        if (this.allStreamsFinished()) {
            this.streamingActive = false;
            this.chatFinished = true;
            this.responseInProgress = false;
            this.streamingStates.clear();
            setTimeout(() => this.scrollToMessage(), 100);
        }
    }

    private getChannelContent(state: StreamingChannelState): string {
        return state.message.text.substring(state.prefix.length);
    }

    private allStreamsFinished(): boolean {
        if (this.streamingStates.size === 0) {
            return false;
        }
        for (const state of this.streamingStates.values()) {
            if (!state.done || state.buffer.length > 0) {
                return false;
            }
        }
        return true;
    }

    private removeSpinnerPlaceholder(): void {
        const hasSpinner = this.messages.some((m) => m.text === '...');
        if (hasSpinner) {
            this.messages = this.messages.filter((m) => m.text !== '...');
        }
    }

    private handleStreamFailure(err: unknown, debugAtSend: boolean, fallbackOverride?: string): void {
        console.error('AI streaming failed', err);
        this.resetStreamingState();
        this.currentResponseSub = null;
        this.messages = this.messages.filter((m) => m.text !== '...');

        const fallback = fallbackOverride ?? 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
        this.messages.push({
            text: debugAtSend ? `ERROR: ${String(err)}` : fallback,
            isUser: false,
            icon: 'triangle-exclamation',
            ts: Date.now(),
        });
        this.chatFinished = true;
        this.responseInProgress = false;
        setTimeout(() => this.scrollToMessage(), 100);
    }

    private handleStreamCompleteSignal(): void {
        const channels = Array.from(this.streamingStates.keys());
        channels.forEach((channel) => {
            const state = this.streamingStates.get(channel);
            if (state && !state.done) {
                this.markStreamingComplete(channel);
            }
        });
        this.currentResponseSub = null;
    }

    private resetStreamingState(): void {
        this.streamingStates.forEach((state) => {
            if (state.interval) {
                clearInterval(state.interval);
            }
            state.message.text = this.normalizeMd(state.message.text);
            state.done = true;
        });
        this.streamingStates.clear();
        this.streamingActive = false;
    }

    private longestCommonPrefixLength(first: string, second: string): number {
        const max = Math.min(first.length, second.length);
        let idx = 0;
        while (idx < max && first.charCodeAt(idx) === second.charCodeAt(idx)) {
            idx += 1;
        }
        return idx;
    }

    /*
     * ────────────────────────────────────────────────────────────────────────────
     * Misc
     * ────────────────────────────────────────────────────────────────────────────
     */

    private cancelOngoingResponse(): void {
        // Abort in-flight HTTP request
        if (this.currentResponseSub) {
            try {
                this.currentResponseSub.unsubscribe();
            } catch {
                /* noop */
            }
            this.currentResponseSub = null;
        }

        if (this.typingInterval) {
            clearInterval(this.typingInterval);
            this.typingInterval = null;
        }
        if (this.currentResponseMsg) {
            this.currentResponseMsg.text = this.normalizeMd(this.currentResponseMsg.text);
            this.currentResponseMsg = null;
        }

        this.resetStreamingState();
        // Remove any spinner placeholders
        this.messages = this.messages.filter((m) => m.text !== '...');
        this.responseInProgress = false;
        this.chatFinished = true;
    }

    closeChat(): void {
        this.previousResponseId = null;
        this.dialogRef.close();
    }

    restart(): void {
        this.chatStarted = false;
        this.chatFinished = false;
        this.responseInProgress = false;
        this.messages = [];
        this.copiedMessageIndex = null;
        this.cancelTts();
        this.stopAudio();
    }

    disableInput(): void {
        this.input.nativeElement.disabled = true;
        /*
         * MatIconButton type may not always expose _elementRef; safeguard if needed
         * @ts-ignore
         */
        // eslint-disable-next-line no-underscore-dangle
        const btnEl = (this.button as unknown as HasPrivateElementRef)?.['_elementRef']?.nativeElement;
        if (btnEl) btnEl.disabled = true;
    }

    openLink(event: MouseEvent): void {
        this.dialogRef.close();
        event.preventDefault();
    }

    autoResize(textarea: HTMLTextAreaElement) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    }

    onInputFocus(): void {
        this.inputFocused = true;
    }

    onInputBlur(): void {
        this.inputFocused = false;
    }

    /**
     * Handles mouse down events on dialog edges to initiate resize operations
     * @param event Mouse event that triggered the resize
     * @param precomputedHandle Optional resize handle computed outside Angular's zone
     */
    private onResizeMouseDown(event: MouseEvent, precomputedHandle?: ResizeHandle): void {
        if (event.button !== 0 || this.resizeSession) return;
        const handle = precomputedHandle ?? this.detectResizeHandle(event);
        if (!handle) return;
        this.startResize(handle, event);
        this.setCursor(this.cursorForHandle(handle));
    }

    /**
     * Updates cursor style when hovering over resize handles
     * @param event Mouse move event
     */
    onResizeHover(event: MouseEvent): void {
        if (this.resizeSession) {
            this.setCursor(this.cursorForHandle(this.resizeSession.handle));
            return;
        }
        const handle = this.detectResizeHandle(event);
        this.setCursor(handle ? this.cursorForHandle(handle) : '');
    }

    /**
     * Resets cursor style when mouse leaves the dialog area
     */
    onResizeLeave(): void {
        if (!this.resizeSession) {
            this.setCursor('');
        }
    }

    /**
     * Identifies which resize handle (if any) is under the mouse cursor
     * @param event Mouse event with position information
     * @returns The identified resize handle or null if not on a handle
     */
    private detectResizeHandle(event: MouseEvent): ResizeHandle | null {
        const pane = this.overlayPane ?? this.resolveOverlayPane();
        if (!pane) return null;

        const rect = pane.getBoundingClientRect();
        const x = event.clientX;
        const y = event.clientY;
        const edge = this.edgeSize;

        const onLeft = x - rect.left <= edge;
        const onRight = rect.right - x <= edge;
        const onTop = y - rect.top <= edge;
        const onBottom = rect.bottom - y <= edge;

        if (onTop && onLeft) return 'top-left';
        if (onTop && onRight) return 'top-right';
        if (onBottom && onLeft) return 'bottom-left';
        if (onBottom && onRight) return 'bottom-right';
        if (onTop) return 'top';
        if (onBottom) return 'bottom';
        if (onLeft) return 'left';
        if (onRight) return 'right';
        return null;
    }

    /**
     * Sets the appropriate cursor style for the host element
     * @param cursor CSS cursor value to apply
     */
    private setCursor(cursor: string): void {
        this.host.nativeElement.style.cursor = cursor || '';
    }

    private initializeResizeListeners(): void {
        if (this.resizeTeardownFns.length > 0) return;
        const hostElement = this.host?.nativeElement;
        if (!hostElement) return;

        const hoverHandler = (event: MouseEvent) => this.onResizeHover(event);
        const leaveHandler = () => this.onResizeLeave();
        const downHandler = (event: MouseEvent) => {
            if (event.button !== 0 || this.resizeSession) return;
            const handle = this.detectResizeHandle(event);
            if (!handle) return;
            this.ngZone.run(() => this.onResizeMouseDown(event, handle));
        };

        this.ngZone.runOutsideAngular(() => {
            hostElement.addEventListener('mousemove', hoverHandler);
            hostElement.addEventListener('mouseleave', leaveHandler);
            hostElement.addEventListener('mousedown', downHandler);
        });

        this.resizeTeardownFns.push(() => hostElement.removeEventListener('mousemove', hoverHandler));
        this.resizeTeardownFns.push(() => hostElement.removeEventListener('mouseleave', leaveHandler));
        this.resizeTeardownFns.push(() => hostElement.removeEventListener('mousedown', downHandler));
    }

    /**
     * Gets the current position of the draggable element
     * @returns Current x,
     */
    private getCurrentDragPosition(): {x: number; y: number} {
        const drag = this.dragRefDirective as unknown as {
            getFreeDragPosition?: () => {x: number; y: number};
            freeDragPosition?: {x: number; y: number};
        } | undefined;
        if (!drag) return {x: 0, y: 0};
        return drag.getFreeDragPosition?.() ?? drag.freeDragPosition ?? {x: 0, y: 0};
    }

    /**
     * Initiates a resize operation when a handle is clicked
     * @param handle The resize handle that was clicked
     * @param event The original mouse event
     */
    startResize(handle: ResizeHandle, event: MouseEvent): void {
        const pane = this.overlayPane ?? this.resolveOverlayPane();
        if (!pane) return;

        event.preventDefault();
        event.stopPropagation();

        const rect = pane.getBoundingClientRect();
        const dragPos = this.getCurrentDragPosition();

        this.resizeSession = {
            handle,
            startX: event.clientX,
            startY: event.clientY,
            rectLeft: rect.left,
            rectTop: rect.top,
            rectRight: rect.right,
            rectBottom: rect.bottom,
            dragStartX: dragPos.x,
            dragStartY: dragPos.y,
        };

        this.ngZone.runOutsideAngular(() => {
            document.addEventListener('mousemove', this.resizeMouseMove);
            document.addEventListener('mouseup', this.resizeMouseUp);
        });

        this.savedUserSelect = document.body.style.userSelect;
        document.body.style.userSelect = 'none';

        this.savedCursor = document.body.style.cursor;
        document.body.style.cursor = this.cursorForHandle(handle);
    }

    /**
     * Handles mouse movement during resize operations to update dialog dimensions
     * @param event Mouse move event with current coordinates
     */
    private onResizeMouseMove(event: MouseEvent): void {
        if (!this.resizeSession) return;

        event.preventDefault();

        const {handle, startX, startY, rectLeft, rectTop, rectRight, rectBottom, dragStartX, dragStartY} = this.resizeSession;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : rectRight;
        const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : rectBottom;

        let newLeft = rectLeft;
        let newRight = rectRight;
        let newTop = rectTop;
        let newBottom = rectBottom;

        if (handle.includes('left')) {
            const maxLeft = Math.max(0, rectRight - this.minDialogWidth);
            newLeft = this.clamp(rectLeft + deltaX, 0, maxLeft);
        }

        if (handle.includes('right')) {
            const minRight = newLeft + this.minDialogWidth;
            newRight = this.clamp(rectRight + deltaX, minRight, Math.max(minRight, viewportWidth));
        }

        if (handle.includes('top')) {
            const maxTop = Math.max(0, rectBottom - this.minDialogHeight);
            newTop = this.clamp(rectTop + deltaY, 0, maxTop);
        }

        if (handle.includes('bottom')) {
            const minBottom = newTop + this.minDialogHeight;
            newBottom = this.clamp(rectBottom + deltaY, minBottom, Math.max(minBottom, viewportHeight));
        }

        const width = Math.max(this.minDialogWidth, newRight - newLeft);
        const height = Math.max(this.minDialogHeight, newBottom - newTop);
        const dragX = dragStartX + (newLeft - rectLeft);
        const dragY = dragStartY + (newTop - rectTop);

        const finalBounds: DialogBounds = {
            left: newLeft,
            top: newTop,
            width,
            height,
        };

        this.applyDialogBounds(finalBounds, {x: dragX, y: dragY});
    }

    /**
     * Finalizes resize operations and cleans up event listeners
     * @param event Optional mouse up event that triggered the stop
     */
    stopResize(event?: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!this.resizeSession) return;

        document.removeEventListener('mousemove', this.resizeMouseMove);
        document.removeEventListener('mouseup', this.resizeMouseUp);

        if (this.savedUserSelect !== null) {
            document.body.style.userSelect = this.savedUserSelect;
        } else {
            document.body.style.removeProperty('user-select');
        }
        if (this.savedCursor !== null) {
            document.body.style.cursor = this.savedCursor;
        } else {
            document.body.style.removeProperty('cursor');
        }

        this.resizeSession = null;
        this.savedCursor = null;
        this.savedUserSelect = null;
        this.setCursor('');
    }

    /**
     * Applies new size and position to the dialog after resize calculations
     * @param bounds New dimensions and position for the dialog
     * @param dragPosition New drag position for the CDK drag directive
     */
    private applyDialogBounds(bounds: DialogBounds, dragPosition: {x: number; y: number}): void {
        const {width, height} = bounds;

        this.dialogRef.updateSize(`${width}px`, `${height}px`);

        const pane = this.overlayPane ?? this.resolveOverlayPane();
        if (pane) {
            pane.style.width = `${width}px`;
            pane.style.height = `${height}px`;
        }

        if (this.dragRefDirective) {
            this.dragRefDirective.freeDragPosition = dragPosition;
        }
        if (this.dragRefDirective?.['_dragRef']) {
            this.dragRefDirective['_dragRef'].setFreeDragPosition(dragPosition);
        }
    }

    /**
     * Finds and caches the overlay pane element for resize operations
     * @returns The overlay pane element or null if not found
     */
    private resolveOverlayPane(): HTMLElement | null {
        const pane = this.host?.nativeElement?.closest('.cdk-overlay-pane') as HTMLElement | null;
        if (pane) {
            this.overlayPane = pane;
        }
        return pane ?? null;
    }

    /**
     * Determines the appropriate CSS cursor style for a given resize handle
     * @param handle The resize handle type
     * @returns CSS cursor value for the handle
     */
    private cursorForHandle(handle: ResizeHandle): string {
        switch (handle) {
            case 'left':
            case 'right':
                return 'ew-resize';
            case 'top':
            case 'bottom':
                return 'ns-resize';
            case 'top-left':
            case 'bottom-right':
                return 'nwse-resize';
            case 'top-right':
            case 'bottom-left':
                return 'nesw-resize';
            default:
                return 'default';
        }
    }

    /**
     * Constrains a value between minimum and maximum bounds
     * @param value The value to constrain
     * @param min Minimum allowed value
     * @param max Maximum allowed value
     * @returns The clamped value
     */
    private clamp(value: number, min: number, max: number): number {
        if (max < min) return min;
        return Math.min(Math.max(value, min), max);
    }

    // Extracts a response id from various possible JSON shapes
    private getResponseId(response: unknown): string | null {
        if (!response || typeof response !== 'object') return null;
        const record = response as Record<string, unknown>;
        const pick = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);

        const top = pick(record['response_id']) ?? pick(record['responseId']) ?? pick(record['id']);
        if (top) return top;

        const data = record['data'];
        if (data && typeof data === 'object') {
            const dataObj = data as Record<string, unknown>;
            return pick(dataObj['response_id']) ?? pick(dataObj['id']);
        }
        return null;
    }

    private normalizeMd(text: string): string { return text ?? ''; }
    private normalizePlain(text: string): string { return text ?? ''; }

    // Returns the text of the AI's ongoing (partially typed) answer, if any
    private getPartialAIAnswer(): string | null {
        const streamState = this.streamingStates.get('restored') ?? this.streamingStates.get('anonymized');
        if (streamState) {
            const content = this.getChannelContent(streamState).trim();
            if (content.length > 0) {
                return content;
            }
        }

        const text = this.currentResponseMsg?.text ?? '';
        const trimmed = typeof text === 'string' ? text.trim() : '';
        return trimmed.length > 0 ? trimmed : null;
    }

    private stripMarkdown(text: string): string {
        if (!text) return '';
        let value = text.replace(/\r\n/g, '\n');

        value = value.replace(/```[\s\S]*?```/g, (block) => block.replace(/```/g, ''));
        value = value.replace(/`([^`]+)`/g, '$1');
        value = value.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1');
        value = value.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');
        value = value.replace(/^\s*>\s?/gm, '');
        value = value.replace(/^#{1,6}\s+/gm, '');
        value = value.replace(/^\s*[-*+]\s+/gm, '');
        value = value.replace(/^\s*\d+\.\s+/gm, '');
        value = value.replace(/~~(.*?)~~/g, '$1');
        value = value.replace(/(\*\*|__)(.*?)\1/g, '$2');
        value = value.replace(/(\*|_)(.*?)\1/g, '$2');
        value = value.replace(/^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/gm, '');
        value = value.replace(/\|/g, ' ');
        value = value.replace(/<[^>]+>/g, '');
        value = value.replace(/[ \t]+\n/g, '\n');
        value = value.replace(/\n{3,}/g, '\n\n');

        return value.trim();
    }

    getMessagePlainText(message: ChatMessage): string {
        return this.stripMarkdown(message?.text ?? '');
    }

    private playAudio(blob: Blob, messageIndex: number): void {
        this.stopAudio();
        const url = URL.createObjectURL(blob);
        const audio = new Audio();
        audio.src = url;
        audio.onended = () => this.cleanupAudio();
        audio.onerror = () => this.cleanupAudio();
        audio.onplay = () => {
            this.audioPlaying = true;
        };
        audio.onpause = () => {
            this.audioPlaying = false;
        };

        audio.play().catch((err) => {
            console.error('Audio playback failed', err);
            this.cleanupAudio();
        });

        this.audioElement = audio;
        this.audioObjectUrl = url;
        this.speakingMessageIndex = messageIndex;
    }

    private canStreamTts(): boolean {
        return typeof MediaSource !== 'undefined' &&
            typeof ReadableStream !== 'undefined' &&
            MediaSource.isTypeSupported('audio/mpeg');
    }

    private playAudioStream(text: string, messageIndex: number): void {
        this.ttsLoadingMessageIndex = messageIndex;

        const mediaSource = new MediaSource();
        const url = URL.createObjectURL(mediaSource);
        const audio = new Audio();
        audio.src = url;
        audio.onended = () => this.cleanupAudio();
        audio.onerror = () => this.cleanupAudio();
        audio.onplay = () => {
            this.audioPlaying = true;
        };
        audio.onpause = () => {
            this.audioPlaying = false;
        };

        this.audioElement = audio;
        this.audioObjectUrl = url;

        // Trigger playback early while the user gesture is still fresh.
        audio.play().catch(() => {
            // Playback will be retried after the first chunk is appended.
        });

        const chunkQueue: Uint8Array[] = [];
        let sourceBuffer: SourceBuffer | null = null;
        let streamDone = false;
        let started = false;
        let cancelled = false;

        const startPlaybackIfNeeded = () => {
            if (started || cancelled) return;
            started = true;
            this.ttsLoadingMessageIndex = null;
            this.speakingMessageIndex = messageIndex;
            audio.play().catch(() => {
                // Ignore playback errors here; user can retry.
            });
        };

        const finalizeIfDone = () => {
            if (!streamDone || !sourceBuffer || sourceBuffer.updating || mediaSource.readyState !== 'open') {
                return;
            }
            try {
                mediaSource.endOfStream();
            } catch (err) {
                // Ignore end-of-stream issues.
            }
        };

        const appendNext = () => {
            if (cancelled || !sourceBuffer || sourceBuffer.updating) return;
            const chunk = chunkQueue.shift();
            if (!chunk) {
                finalizeIfDone();
                return;
            }
            /*
             * try {
             *     sourceBuffer.appendBuffer(chunk);
             * } catch (err) {
             *     console.error('TTS append failed', err);
             *     this.cleanupAudio();
             * }
             */
        };

        this.ttsStreamCancel = () => {
            cancelled = true;
            if (mediaSource.readyState === 'open') {
                try {
                    mediaSource.endOfStream();
                } catch (err) {
                    // Ignore shutdown errors.
                }
            }
        };

        mediaSource.addEventListener('sourceopen', () => {
            if (cancelled || mediaSource.readyState !== 'open') return;
            try {
                sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
            } catch (err) {
                console.error('TTS source buffer failed', err);
                this.cleanupAudio();
                return;
            }

            sourceBuffer.addEventListener('updateend', () => {
                if (cancelled) return;
                startPlaybackIfNeeded();
                appendNext();
            });

            sourceBuffer.addEventListener('error', () => {
                console.error('TTS source buffer error');
                this.cleanupAudio();
            });

            this.ttsRequestSub = this.backendService
                .postBinaryStream('ArtificialIntelligence/tts', {text})
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (chunk) => {
                        if (cancelled) return;
                        chunkQueue.push(chunk);
                        appendNext();
                    },
                    error: (err) => {
                        this.ttsRequestSub = null;
                        this.ttsLoadingMessageIndex = null;
                        console.error('AI TTS failed', err);
                        this.cleanupAudio();
                    },
                    complete: () => {
                        this.ttsRequestSub = null;
                        streamDone = true;
                        appendNext();
                    },
                });
        }, {once: true});
    }

    private stopAudio(): void {
        if (this.audioElement) {
            this.audioElement.pause();
            this.audioElement.currentTime = 0;
        }
        this.cleanupAudio();
    }

    private cleanupAudio(): void {
        this.cancelTts();
        if (this.audioElement) {
            this.audioElement.src = '';
            this.audioElement.load();
        }
        if (this.audioObjectUrl) {
            URL.revokeObjectURL(this.audioObjectUrl);
        }
        this.audioElement = null;
        this.audioObjectUrl = null;
        this.audioPlaying = false;
        this.speakingMessageIndex = null;
    }

    mdWithBr(text: string): string {
        /*
         * Preserve newlines for proper Markdown parsing (e.g., GFM tables need raw \n).
         * Only normalize CRLF to LF; do NOT replace \n with <br>.
         */
        return (text ?? '').replace(/\r\n/g, '\n');
    }
}
